import React from 'react'
import { Modal, Button, Radio, Space, Empty } from 'antd'
import { FormattedMessage } from 'react-intl'
import Loading from '../components/Loading'
import { useSalesCodeConstraints } from '../hooks/SalesCodes'

// Groups a flat list of sales-code constraints by their group name.
// Returns { [groupName]: { name, description, constraints: [...] } }
function groupBySalesCodeGroup(salesCodeConstraints) {
  return (salesCodeConstraints ?? []).reduce((acc, constraint) => {
    const key = constraint.group.name
    const existing = acc[key] ?? {
      name: key,
      description: constraint.group.description,
      constraints: [],
    }
    existing.constraints.push(constraint)
    return { ...acc, [key]: existing }
  }, {})
}

const AddStationGroupModal = ({ open, onCancel, onConfirm, makeModelId }) => {
  const { salesCodeConstraints, isLoading } = useSalesCodeConstraints({
    makeModelID: makeModelId,
  })
  const [selectedGroupName, setSelectedGroupName] = React.useState(null)

  const groups = React.useMemo(
    () => Object.values(groupBySalesCodeGroup(salesCodeConstraints)),
    [salesCodeConstraints],
  )

  // Reset the selection whenever the modal is reopened.
  React.useEffect(() => {
    if (open) {
      setSelectedGroupName(null)
    }
  }, [open])

  const onSubmit = () => {
    const group = groups.find((g) => g.name === selectedGroupName)
    if (group) {
      onConfirm(group)
    }
  }

  return (
    <Modal
      open={open}
      title={<FormattedMessage id="globalSearch.stations.selectGroupTitle" />}
      onCancel={onCancel}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          <FormattedMessage id="forms.cancel" />
        </Button>,
        <Button
          key="submit"
          type="primary"
          onClick={onSubmit}
          disabled={selectedGroupName === null}
          data-testid="select-sales-code-group-submit"
        >
          <FormattedMessage id="forms.submit" />
        </Button>,
      ]}
      centered={true}
    >
      {isLoading ? (
        <Loading useBackgroundColor={false} />
      ) : groups.length === 0 ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        <Radio.Group
          onChange={(e) => setSelectedGroupName(e.target.value)}
          value={selectedGroupName}
        >
          <Space direction="vertical">
            {groups.map((group, index) => (
              <Radio
                key={group.name}
                value={group.name}
                data-testid={`sales-code-group-${index}`}
              >
                {group.description || group.name}
              </Radio>
            ))}
          </Space>
        </Radio.Group>
      )}
    </Modal>
  )
}

export default AddStationGroupModal
